import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import {
    GrispiNewsNotificationType,
    GrispiNewsNotificationTypeMap,
    GrispiNewsResponseType,
    GrispiNewsTicketActionTypeMap,
    GrispiNewsTicketNotificationType
} from '../../containers/GrispiNews/grispi-news.type';
import {
    getTicketKeyFromChannel,
    isChatMessage,
    isSessionNews,
    isTenantNews,
    isTicketNews,
    isUserNews
} from '../../containers/GrispiNews/grispi-news.utils';
import { isSSR } from '../../utils/helpers';
import { getActiveTicketStore } from '../../utils/store/getStore';
import uuid from '../../utils/uuid';

enableStaticRendering(isSSR());

export class GrispiNewsStore {
    isGrispiNewsApiAlive: boolean;
    notifications: GrispiNewsNotificationType[];
    ticketNotifications: GrispiNewsTicketNotificationType[];
    constructor() {
        this.isGrispiNewsApiAlive = false;
        this.notifications = [];
        this.ticketNotifications = [];
        // Call it here
        makeAutoObservable(this);
    }
    get getActiveTicketNotifications() {
        const activeTicketKey = getActiveTicketStore().activeTicket?.key;
        if (activeTicketKey) {
            return this.ticketNotifications.filter((ticket) => ticket.tabKey === activeTicketKey).toReversed();
        }
        return [];
    }

    addNotification = (notification: GrispiNewsResponseType): void => {
        if (!notification) return;
        const newNotificationId = uuid();
        if (isChatMessage(notification.c)) {
            const activeChatMessage = this.notifications.find(
                (n) => n.ticketKey === notification.d.k && n.type === GrispiNewsNotificationTypeMap.CHAT_MESSAGE
            );
            if (activeChatMessage) {
                runInAction(() => {
                    this.notifications = this.notifications.filter((n) => n.id !== activeChatMessage.id);
                });
            }
            runInAction(() => {
                this.notifications.push({
                    id: newNotificationId,
                    channel: notification.c,
                    action: GrispiNewsTicketActionTypeMap.u,
                    ticketKey: notification.d.k,
                    isSeen: false,
                    type: GrispiNewsNotificationTypeMap.CHAT_MESSAGE,
                    eventDate: notification.t,
                    user: notification.u,
                    message: notification.d.message
                });
            });
            return;
        }
        if (isTicketNews(notification.c)) {
            runInAction(() => {
                this.notifications.push({
                    id: newNotificationId,
                    channel: notification.c,
                    action: notification.d.a,
                    ticketKey: notification.d.k ?? getTicketKeyFromChannel(notification.c),
                    isSeen: false,
                    type: GrispiNewsNotificationTypeMap.TICKET_NEWS,
                    eventDate: notification.t,
                    user: notification.u
                });
            });
            runInAction(() => {
                this.ticketNotifications.push({
                    id: newNotificationId,
                    tabKey: getTicketKeyFromChannel(notification.c),
                    action: notification.d.a,
                    fields: notification.d.fields,
                    eventDate: notification.t,
                    isNeedUpdate:
                        notification.d.a === GrispiNewsTicketActionTypeMap.u ||
                        notification.d.a === GrispiNewsTicketActionTypeMap.c,
                    isSeen: false,
                    user: notification.u
                });
            });
            return;
        }
        //user news
        if (isUserNews(notification.c)) {
            runInAction(() => {
                this.notifications.push({
                    id: newNotificationId,
                    channel: notification.c,
                    action: notification.d.a,
                    ticketKey: notification.d.k,
                    isSeen: false,
                    type: GrispiNewsNotificationTypeMap.USER_NEWS,
                    eventDate: notification.t,
                    user: notification.u
                });
            });
            return;
        }
        //tenant news
        if (isTenantNews(notification.c)) {
            console.log('Grispi news tenant news: ', notification);
            return;
        }
        //session news
        if (isSessionNews(notification.c)) {
            console.log('Grispi news session news: ', notification);
            return;
        }
    };

    removeNotificationById = (id: string): void => {
        runInAction(() => {
            this.notifications = this.notifications.filter((n) => n.id !== id);
        });
    };

    removeTicketNotificationsByTabKey = (tabKey: string): void => {
        runInAction(() => {
            this.ticketNotifications = this.ticketNotifications.filter((n) => n.tabKey !== tabKey);
        });
    };

    removeTicketNotificationsByTicketKeyAndAction = (
        ticketKey: string,
        action: GrispiNewsTicketActionTypeMap
    ): void => {
        runInAction(() => {
            this.ticketNotifications = this.ticketNotifications.filter(
                (n) => !(n.tabKey === ticketKey && n.action === action)
            );
        });
    };
    removeTicketNotificationById = (id: string): void => {
        runInAction(() => {
            this.ticketNotifications = this.ticketNotifications.filter((n) => n.id !== id);
        });
    };
    setAllNotificationsAsSeen = () => {
        runInAction(() => {
            this.notifications = this.notifications.map((n) => ({ ...n, isSeen: true }));
        });
    };

    removeAllNotifications = () => {
        runInAction(() => {
            this.notifications = [];
        });
    };

    setAllTicketNotificationsAsUpdatedByTicketKey = (ticketKey) => {
        runInAction(() => {
            this.ticketNotifications = this.ticketNotifications.map((n) => {
                if (n.tabKey === ticketKey) return { ...n, isNeedUpdate: false };
                else return n;
            });
        });
    };
    setAllTicketNotificationsAsSeenByTicketKey = (ticketKey) => {
        runInAction(() => {
            this.ticketNotifications = this.ticketNotifications.map((n) => {
                if (n.tabKey === ticketKey) return { ...n, isSeen: true };
                else return n;
            });
        });
    };

    setGrispiNewsApiStatus = (health: boolean) => {
        runInAction(() => {
            this.isGrispiNewsApiAlive = health;
        });
    };
}
